import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToasterService } from '../services/toaster/toaster.service';
import { config } from '../config';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

interface PlanFeatures {
  clients: number;
  consultants: number;
  features: string[];
}

interface Plan {
  id: number;
  name: string;
  description: string;
  stripe_price_id: string;
  stripe_product_id: string;
  amount: string;
  currency: string;
  interval: string;
  active: boolean;
  features: string;
  created_at: string;
  updated_at: string;
  parsedFeatures?: PlanFeatures;
  popular?: boolean;
}

interface Transaction {
  id: string;
  date: string;
  amount: number;
  status: string;
  plan: string;
}

interface Subscription {
  id: string;
  plan: string;
  status: string;
  amount: number;
  billingCycle: string;
  nextBillingDate: Date;
  startDate: Date;
  lastFourDigits: string;
  paymentMethod: string;
}

@Component({
  selector: 'app-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.sass']
})
export class BillingComponent implements OnInit {
  // Tab navigation
  activeTabIndex: number = 0;
  
  // Plans data
  plans: Plan[] = [];
  isLoadingPlans: boolean = false;

  // Payment flow
  selectedPlan: Plan | null = null;
  isProcessing: boolean = false;
  
  // Transaction history
  transactionHistory: Transaction[] = [];
  transactionDataSource: MatTableDataSource<Transaction>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  
  // Current subscription
  hasActiveSubscription: boolean = true; // Set to true for demo purposes
  currentSubscription: Subscription;
  currentSubscriptionFeatures: string[] = [];
  
  // Cancellation dialog
  showCancelDialog: boolean = false;
  isCancelling: boolean = false;
  cancellationReason: string = '';

  // Stripe integration
  stripe: any = null;

  constructor(
    private http: HttpClient,
    private toasterService: ToasterService,
    private router: Router,
  ) {}

  ngOnInit() {
    // Load plans from API
    this.loadPlans();
    
    // Load transaction history
    this.loadTransactionHistory();
    
    // Load current subscription (mock data)
    this.loadCurrentSubscription();
  }
  
  /**
   * Load plans from API
   */
   loadPlans(): void {
    this.isLoadingPlans = true;
    
    this.http.get<Plan[]>(`${config.apiUrl}/billing/plans`)
      .subscribe({
        next: (plans) => {
          // Parse the features JSON string for each plan
          this.plans = plans.map(plan => {
            try {
              // Parse the features JSON string
              const parsedFeatures = JSON.parse(plan.features) as PlanFeatures;
              
              // Mark the most expensive plan as popular
              const popular = plan.name === 'Enterprise';
              
              return {
                ...plan,
                parsedFeatures,
                popular
              };
            } catch (e) {
              console.error(`Failed to parse features for plan ${plan.name}`, e);
              return {
                ...plan,
                parsedFeatures: { clients: 0, consultants: 0, features: [] },
                popular: false
              };
            }
          });
          
          this.isLoadingPlans = false;
          
          // If we have a current subscription, update the features
          if (this.currentSubscription) {
            this.updateSubscriptionFeatures();
          }
        },
        error: (error) => {
          console.error('Failed to load plans', error);
          this.toasterService.show('error', 'Failed to load subscription plans. Please refresh the page.');
          this.isLoadingPlans = false;
        }
      });
  }
  
  ngAfterViewInit() {
    if (this.transactionDataSource) {
      this.transactionDataSource.paginator = this.paginator;
    }
  }
  
  /**
   * Handle tab changes
   */
  onTabChange(index: number) {
    this.activeTabIndex = index;
    
    // Reset payment flow when switching tabs
    if (this.selectedPlan) {
      this.goBack();
    }
    
    // Close cancel dialog if open
    if (this.showCancelDialog) {
      this.closeCancelDialog();
    }
  }
  
  /**
   * Load transaction history from API
   * This is a mock implementation
   */
  private loadTransactionHistory(): void {
    // In a real app, this would be an API call
    this.transactionHistory = [
      {
        id: 'txn_1234567890',
        date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
        amount: 199,
        status: 'Completed',
        plan: 'Premium'
      },
      {
        id: 'txn_0987654321',
        date: new Date(Date.now() - 37 * 24 * 60 * 60 * 1000).toISOString(), // 37 days ago
        amount: 99,
        status: 'Completed',
        plan: 'Basic'
      },
      {
        id: 'txn_5678901234',
        date: new Date(Date.now() - 67 * 24 * 60 * 60 * 1000).toISOString(), // 67 days ago
        amount: 99,
        status: 'Completed',
        plan: 'Basic'
      }
    ];
    
    // Initialize the data source for the table
    this.transactionDataSource = new MatTableDataSource(this.transactionHistory);
  }
  
  /**
   * Load current subscription data
   * This is a mock implementation
   */
  private loadCurrentSubscription(): void {
    // In a real app, this would be an API call
    this.currentSubscription = {
      id: 'sub_1234567890',
      plan: 'Premium',
      status: 'Active',
      amount: 199,
      billingCycle: 'year',
      nextBillingDate: new Date(Date.now() + 358 * 24 * 60 * 60 * 1000), // ~1 year from now
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      lastFourDigits: '4242',
      paymentMethod: 'Visa'
    };
    
    // Update subscription features if plans are already loaded
    this.updateSubscriptionFeatures();
  }
  
  /**
   * Update subscription features based on current plan
   */
  private updateSubscriptionFeatures(): void {
    if (this.plans.length > 0 && this.currentSubscription) {
      const subscriptionPlan = this.plans.find(p => p.name === this.currentSubscription.plan);
      if (subscriptionPlan && subscriptionPlan.parsedFeatures) {
        this.currentSubscriptionFeatures = subscriptionPlan.parsedFeatures.features;
      }
    }
  }

  /**
   * Select a plan for purchase
   */
  selectPlan(plan: Plan) {
    // Make sure the features are parsed
    if (plan && !plan.parsedFeatures && plan.features) {
      try {
        plan.parsedFeatures = JSON.parse(plan.features) as PlanFeatures;
      } catch (e) {
        console.error(`Failed to parse features for plan ${plan.name}`, e);
        plan.parsedFeatures = { clients: 0, consultants: 0, features: [] };
      }
    }
    
    this.selectedPlan = plan;
  }

  /**
   * Process payment for selected plan using Stripe Checkout
   */
  async processPayment() {
    if (!this.selectedPlan) {
      this.toasterService.show('warning', 'Please select a plan first');
      return;
    }

    if (this.isLoadingPlans) {
      this.toasterService.show('warning', 'Please wait while plans are being loaded');
      return;
    }

    this.isProcessing = true;

    try {
      // Create the checkout session with success and cancel URLs
      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = `${window.location.origin}/billing/cancel`;
      
      const response = await this.http.post<{url: string}>(`${config.apiUrl}/billing/create-checkout-session`, {
        priceId: this.selectedPlan.stripe_price_id,
        success_url: successUrl,
        cancel_url: cancelUrl
      }).toPromise();

      // Redirect to the Stripe Checkout page
      window.location.href = response.url;
      
    } catch (error) {
      console.error('Failed to create checkout session', error);
      this.toasterService.show('error', 'Payment system is not available. Please try again later.');
      this.isProcessing = false;
    }
  }

  /**
   * Go back to plan selection
   */
  goBack() {
    this.selectedPlan = null;
  }
  
  /**
   * Get the date one year from now for the billing cycle
   */
  getNextYearDate(): Date {
    const today = new Date();
    return new Date(today.setFullYear(today.getFullYear() + 1));
  }
  
  /**
   * Get the date one month from now for the billing cycle
   */
  getNextMonthDate(): Date {
    const today = new Date();
    return new Date(today.setMonth(today.getMonth() + 1));
  }

  /**
   * View receipt for a transaction
   */
  viewReceipt(transaction: Transaction) {
    // Now handled by opening a modal or navigating to a receipt page
    this.toasterService.show('info', 'Receipt viewing will be available soon.');
  }
  
  /**
   * Download receipt as PDF
   * This is a mock implementation
   */
  downloadReceipt(transaction: Transaction) {
    // Simulate download delay
    setTimeout(() => {
      this.toasterService.show('success', 'Receipt downloaded successfully!');
    }, 1500);
  }
  
  /**
   * Update payment method
   * This is a mock implementation
   */
  updatePaymentMethod() {
    this.toasterService.show('info', 'Payment method update feature will be available soon.');
  }
  
  /**
   * Change subscription plan
   */
  changePlan() {
    this.activeTabIndex = 0; // Switch to plans tab
  }
  
  /**
   * Open the cancel subscription dialog
   */
  openCancelDialog() {
    this.showCancelDialog = true;
    this.cancellationReason = '';
  }
  
  /**
   * Close the cancel subscription dialog
   */
  closeCancelDialog() {
    this.showCancelDialog = false;
  }
  
  /**
   * Confirm subscription cancellation
   */
  async confirmCancellation() {
    if (!this.cancellationReason) {
      this.toasterService.show('warning', 'Please select a reason for cancellation');
      return;
    }
    
    this.isCancelling = true;
    
    try {
      // In a real app, this would call an API to cancel the subscription
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      
      // Update subscription status
      this.currentSubscription.status = 'Cancelled';
      
      this.toasterService.show('success', 'Your subscription has been cancelled. You will have access until the end of your billing period.');
      this.closeCancelDialog();
    } catch (error) {
      console.error('Cancellation failed', error);
      this.toasterService.show('error', 'Failed to cancel subscription. Please try again.');
    } finally {
      this.isCancelling = false;
    }
  }
}